import { useState } from "react"
import PropTypes from "prop-types"
import { Button, Card, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Text } from "ui/Typography"
import { spacings } from "themes/variables"
import { toaster } from "components/common/Toast"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"
import { removePowerTranzSavedCard } from "store/payment-provider"
import { mapSavedCard } from "store/powertranzApi"
import { getPowerTranzErrorCode, getPowerTranzErrorMessage } from "helpers/powertranz"

// The client's card saved with the guide's PowerTranz merchant account for this booking's balance.
// Only brand and last four are ever shown; removing it means the balance is collected by link.
const PowerTranzSavedCard = ({ savedCard: savedCardPayload }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { showConfirm, modal } = useNewConfirmModal()
  const isRemoving = useSelector((state) => state.paymentProvider.removePowerTranzSavedCard.loading)
  const [isRemoved, setIsRemoved] = useState(false)
  const savedCard = mapSavedCard(savedCardPayload)

  if (!savedCard?.id || isRemoved) return null

  const handleRemoveConfirm = async () => {
    try {
      await dispatch(removePowerTranzSavedCard(savedCard.id))
      setIsRemoved(true)
      toaster.success(t("powertranz.saved_card.removed"))
    } catch (error) {
      toaster.error(getPowerTranzErrorMessage(t, getPowerTranzErrorCode(error)))
    }
  }

  const handleRemoveRequest = () => {
    showConfirm(
      {
        message: t("powertranz.saved_card.remove_confirmation", { lastFour: savedCard.lastFour }),
        confirmLabel: t("powertranz.saved_card.remove"),
        cancelLabel: t("global.cancel")
      },
      handleRemoveConfirm
    )
  }

  return (
    <Card className="mt-m">
      <Flex justify="space-between" align="center" gap={spacings.S} wrap>
        <Flex vertical gap={spacings.XXS}>
          <Text strong>{t("powertranz.saved_card.title")}</Text>
          <Text>{t("powertranz.saved_card.card", { brand: savedCard.brand, lastFour: savedCard.lastFour })}</Text>
        </Flex>
        <Button danger onClick={handleRemoveRequest} loading={isRemoving} disabled={isRemoving}>
          {t("powertranz.saved_card.remove")}
        </Button>
      </Flex>
      {modal}
    </Card>
  )
}

PowerTranzSavedCard.propTypes = {
  // Raw API payload: { id, brand, last_four }
  savedCard: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    brand: PropTypes.string,
    last_four: PropTypes.string
  })
}

export default PowerTranzSavedCard
